import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import type { WarehouseSection } from '../lib/types/manufacturing';

// ============================================
// TYPES
// ============================================

interface WarehouseModeContextType {
  // Warehouse mode state
  isWarehouseMode: boolean;
  setWarehouseMode: (enabled: boolean) => void;

  // Active section in the sidebar
  activeSection: WarehouseSection;
  setActiveSection: (section: WarehouseSection) => void;

  // Mobile drawer state
  mobileWarehouseDrawerOpen: boolean;
  setMobileWarehouseDrawerOpen: (open: boolean) => void;
}

// ============================================
// CONTEXT
// ============================================

const WarehouseModeContext = createContext<WarehouseModeContextType | undefined>(undefined);

// ============================================
// PROVIDER
// ============================================

interface WarehouseModeProviderProps {
  children: ReactNode;
}

export function WarehouseModeProvider({ children }: WarehouseModeProviderProps) {
  // Inizializza basandosi sulla URL corrente per evitare flash del menu CRM
  const [isWarehouseMode, setIsWarehouseMode] = useState(
    () => window.location.pathname.startsWith('/magazzino')
  );
  const [activeSection, setActiveSectionState] = useState<WarehouseSection>('dashboard');
  const [mobileWarehouseDrawerOpen, setMobileWarehouseDrawerOpen] = useState(false);

  const setWarehouseMode = useCallback((enabled: boolean) => {
    setIsWarehouseMode(enabled);
    if (!enabled) {
      setMobileWarehouseDrawerOpen(false);
    }
  }, []);

  const setActiveSection = useCallback((section: WarehouseSection) => {
    setActiveSectionState(section);
  }, []);

  const value: WarehouseModeContextType = useMemo(() => ({
    isWarehouseMode,
    setWarehouseMode,
    activeSection,
    setActiveSection,
    mobileWarehouseDrawerOpen,
    setMobileWarehouseDrawerOpen,
  }), [
    isWarehouseMode,
    setWarehouseMode,
    activeSection,
    setActiveSection,
    mobileWarehouseDrawerOpen,
  ]);

  return (
    <WarehouseModeContext.Provider value={value}>
      {children}
    </WarehouseModeContext.Provider>
  );
}

// ============================================
// HOOKS
// ============================================

export function useWarehouseMode() {
  const context = useContext(WarehouseModeContext);
  if (context === undefined) {
    throw new Error('useWarehouseMode must be used within a WarehouseModeProvider');
  }
  return context;
}

/**
 * Safe version that returns null if no provider is present.
 * Used by Layout.tsx which may render before provider is mounted.
 */
export function useWarehouseModeSafe(): WarehouseModeContextType | null {
  const context = useContext(WarehouseModeContext);
  return context ?? null;
}
